import React, { useEffect, useMemo, useState } from 'react';
import { Button, Col, Icon, Row, Card, Popconfirm, message, Tag, Alert, Result } from 'antd';
import {
	createAddOns,
	deleteAddOns,
	deleteOrganization,
	getConfig,
	getOrganization,
	getUsers,
	deleteAddOn,
	restoreOrganization,
	updateOrganization,
} from '../../utils/api';
import OrganizationModal from '../../components/organization/organizationModal';
import AddOnTags from '../../components/AddOnTags';
import UserList from '../../components/user/userList';
import { buildAddOns } from '../../utils/addOnsHelper';
import EliteratureSelector from '../../components/eliterature/eliteratureSelector';
import SocratesManagement from '../../components/socrates/socratesManagement';

const OrganizationComponent = ({ match }) => {
	const [organization, setOrganization] = useState(null);
	const [availableDatabases, setAvailableDatabases] = useState([]);
	const [editOrg, setEditOrgOpen] = useState(false);
	const [loaded, setLoaded] = useState(false);

	const { orgId } = match.params;

	const orgObject = useMemo(() => {
		if (organization) {
			return {
				name: organization.name,
				address: organization.address,
				zipCode: organization.zipcode,
				phone: organization.phone,
				city: organization.city,
				databases: organization.allowedDatabase,
				licenseKey: organization.licenseKey,
				expiryDate: organization.expiryDate,
				addOns: organization.addOns,
			};
		} else {
			return {};
		}
	}, [organization]);

	const loadOrganization = async () => {
		try {
			setLoaded(false);
			const res = (await getOrganization(orgId)) || null;
			const org = {
				...res,
				addOns: buildAddOns(res),
			};

			setOrganization(org);
		} catch (e) {
			setOrganization(null);
		}
		setLoaded(true);
	};

	const handleOrganizationModalSubmit = async (formData) => {
		const { addOns, ...formValues } = formData;

		const organizationBody = {
			name: formValues.name || organization.name,
			address: formValues.address || organization.address,
			zipCode: formValues.zipCode || organization.zipCode,
			phone: formValues.phone || organization.phone,
			city: formValues.city || organization.city,
			allowedDatabase: formValues.databases.join('') || organization.allowedDatabase,
			expiryDate: formValues.expiryDate
				? formValues.expiryDate.format('YYYY-MM-DD')
				: organization.expiryDate,
			licenseKey: formValues.licenseKey,
			featureRMHandbook: (addOns && addOns.includes('RM_HANDBOOK')) || false,
			featureLegalComments: (addOns && addOns.includes('LEGAL_COMMENTS')) || false,
			featureELiterature: (addOns && addOns.includes('E_LITERATURE')) || false,
			featureCompliance: (addOns && addOns.includes('COMPLIANCE')) || false,
			sbtPlus: (addOns && addOns.includes('LEGAL_CASE_PLUS')) || false,
		};

		const handleAddOns = async (accountID) => {
			if (addOns) {
				const addOnData = addOns.map((addOn) => ({
					accountID,
					accountType: 'ORGANIZATION',
					type: addOn,
				}));

				const addOnsToRemove = organization.addOns
					.filter((existingAddOn) => !addOns.includes(existingAddOn.type))
					.map((existingAddOn) => existingAddOn.id);

				if (addOnsToRemove.length) {
					await deleteAddOns(addOnsToRemove);
				}

				const addOnResponse = await createAddOns(addOnData);
				if (!addOnResponse) {
					message.error('Misslyckades att skapa tillägg. Kontakta support.');
				}
			}
		};

		try {
			const updatedOrg = await updateOrganization(orgId, organizationBody);
			if (updatedOrg.id) {
				message.success(`Kund ${updatedOrg.id} uppdaterad`);
				await handleAddOns(updatedOrg.id);

				// Remove Socrates add-on from all users for this organization
				getUsers(updatedOrg.id).then((users) => {
					users.forEach((user) => {
						let socratesAddOn = user.addOns.filter((addOn) => addOn.type === 'SOCRATES');

						if (socratesAddOn.length > 0) {
							deleteAddOn(socratesAddOn[0].id);
						}
					});
				});
			}
		} catch (error) {
			message.error(`Kunde inte uppdatera kund`);
		}

		await loadOrganization();
	};

	const handleOrganizationDelete = async () => {
		try {
			await deleteOrganization(organization.id);

			message.success(`Kund markerad för radering`);
			await loadOrganization();
		} catch (error) {
			message.error('Kunde inte radera kund');
		}
	};

	const handleOrganizationRestore = async () => {
		try {
			await restoreOrganization(organization.id);

			message.success(`Kund återställd`);
			await loadOrganization();
		} catch (error) {
			message.error('Kunde inte återställa kund');
		}
	};

	const hasAddon = (addon) => {
		if (!organization) return false;

		const addOnTypes = organization.addOns.map((addOn) => addOn.type);

		return addOnTypes.includes(addon);
	};

	useEffect(() => {
		const loadConfig = async () => {
			if (!availableDatabases.length) {
				const config = await getConfig();
				if (config && config.databases) {
					setAvailableDatabases(
						config.databases.filter((db) => !['c', 'y'].includes(db.key.toLowerCase()))
					);
				}
			}
		};

		loadConfig();
		if (!organization) {
			loadOrganization();
		}
	}, [orgId]);

	if (!organization && loaded) {
		return (
			<Card style={{ height: '100%' }}>
				<Result
					status='404'
					title='404'
					subTitle={`Tyvärr, kunde inte hitta kund ${orgId}.`}
					extra={
						<Button
							type='primary'
							icon='arrow-left'
							size='large'
							onClick={() => window.location.assign('/kunder')}
						>
							Tillbaka
						</Button>
					}
				/>
			</Card>
		);
	}

	return (
		<div>
			{/* Organization Modal */}
			<OrganizationModal
				mode={'edit'}
				organization={orgObject}
				open={editOrg}
				onCancel={() => setEditOrgOpen(false)}
				onSubmit={handleOrganizationModalSubmit}
			/>

			{/* Header Buttons */}
			<Row style={{ marginBottom: 16 }}>
				<Col span={8}>
					<Button
						type='secondary'
						icon='arrow-left'
						size='large'
						onClick={() => window.location.assign('/kunder')}
					>
						Tillbaka
					</Button>
				</Col>
				<Col span={16}>
					<div style={{ float: 'right' }}>
						<Button
							type='primary'
							size='large'
							icon='edit'
							style={{ marginRight: 8 }}
							onClick={() => setEditOrgOpen(true)}
						>
							Ändra
						</Button>
						{organization && organization.deletionDate && (
							<Button
								type='default'
								size='large'
								icon='undo'
								style={{ marginRight: 8 }}
								onClick={handleOrganizationRestore}
							>
								Återställ
							</Button>
						)}
						{organization && !organization.deletionDate && (
							<Popconfirm
								title={
									<span>
										Är du säker på att du vill radera <b>{organization.name}</b>?
									</span>
								}
								onConfirm={handleOrganizationDelete}
								okText='Ja'
								cancelText='Nej'
								okType='danger'
								okButtonProps={{ size: 'default' }}
								cancelButtonProps={{ size: 'default' }}
								placement='bottomRight'
								icon={<Icon type='delete' style={{ color: 'red' }} />}
							>
								<Button type='danger' size='large' icon='delete'>
									Radera
								</Button>
							</Popconfirm>
						)}
					</div>
				</Col>
			</Row>

			{/* Organization Details */}
			<Card>
				{organization && organization.deletionDate && (
					<Row style={{ marginBottom: 16 }}>
						<Alert
							message={
								<span>
									Kund markerad för borttagning. Raderas <b>{organization.deletionDate}</b>
								</span>
							}
							type='warning'
							showIcon
						/>
					</Row>
				)}
				<Row style={{ marginBottom: 16 }}>
					<h2>{organization && organization.name}</h2>
				</Row>

				<Row style={{ marginBottom: 32 }} gutter={[16, 16]} type='flex'>
					<Col xxl={6} xl={8} lg={12} md={24} sm={24}>
						<Card title='Uppgifter' bodyStyle={{ height: '100%' }} style={{ height: '100%' }}>
							<Row gutter={[16, 16]} type='flex'>
								<Col span={24}>
									<b>Telefon:</b> {(organization && organization.phone) || '-'}
								</Col>
								<Col span={24}>
									<b>Adress:</b> {(organization && organization.address) || '-'}
									{organization &&
										organization.city &&
										organization.zipcode &&
										`, ${organization.zipcode} ${organization.city}`}
								</Col>
							</Row>
						</Card>
					</Col>
					<Col xxl={6} xl={8} lg={12} md={24} sm={24}>
						<Card title='Licens' bodyStyle={{ height: '100%' }} style={{ height: '100%' }}>
							<Row gutter={[16, 16]} type='flex'>
								<Col span={24}>
									<b>Utgångsdatum:</b> {(organization && organization.expiryDate) || '∞'}
								</Col>
								<Col span={24}>
									<b>Antal konton:</b> {(organization && organization.maxAccounts) || '∞'}
								</Col>
							</Row>
						</Card>
					</Col>
					<Col xxl={6} xl={8} lg={12} md={24} sm={24}>
						<Card title='Databaser' bodyStyle={{ height: '100%' }} style={{ height: '100%' }}>
							<span style={{ display: 'flex', flexWrap: 'wrap', rowGap: 8 }}>
								{availableDatabases.length &&
									organization &&
									organization.allowedDatabase &&
									availableDatabases
										.filter((item) =>
											Array.from(organization.allowedDatabase.toLowerCase()).includes(
												item.key.toLowerCase()
											)
										)
										.map((database) => (
											<Tag key={database.key} style={{}}>
												{database.name} <b>({database.key})</b>
											</Tag>
										))}
							</span>
						</Card>
					</Col>
					<Col xxl={6} xl={8} lg={12} md={24} sm={24}>
						<Card title='Plustjänster' bodyStyle={{ height: '100%' }} style={{ height: '100%' }}>
							{organization && <AddOnTags addOns={organization.addOns} />}
						</Card>
					</Col>
				</Row>

				{/* User List */}
				<Row>
					<UserList
						orgId={orgId}
						createNew
						title='Konton'
						onChange={() => {
							loadOrganization();
						}}
					/>
				</Row>

				{loaded && (
					<Row style={{ marginTop: 16 }}>
						<SocratesManagement organizationId={Number(orgId)} enabled={hasAddon('SOCRATES')} />
					</Row>
				)}

				{hasAddon('E_LITERATURE') && (
					<Row style={{ marginTop: 16 }}>
						<EliteratureSelector organizationId={Number(orgId)} />
					</Row>
				)}
			</Card>
		</div>
	);
};

export default OrganizationComponent;



// WEBPACK FOOTER //
// ./src/pages/organization/organization.jsx